import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface NavigationContextType {
  pathname: string;
  navigate: (path: string) => void;
}

const NavigationContext = createContext<NavigationContextType | null>(null);

interface NavigationProviderProps {
  children: ReactNode;
}

export const NavigationProvider: React.FC<NavigationProviderProps> = ({ children }) => {
  const [pathname, setPathname] = useState(window.location.pathname);

  // Update pathname when browser back/forward buttons are used
  useEffect(() => {
    const handlePopState = () => {
      setPathname(window.location.pathname);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigate = (path: string) => {
    // Extract just the pathname part (ignore query params for comparison)
    const newPathname = path.includes('?') ? path.split('?')[0] : path;
    if (newPathname === pathname) return; // Don't navigate if already on the same path
    
    // Update browser URL without refresh
    window.history.pushState({}, '', path);

    // pushState keeps the old scroll position, so a link from partway down the page would land
    // partway down the new one. Back/forward (popstate) is left alone — the browser restores
    // scroll for those itself.
    window.scrollTo(0, 0);

    // Update React state to trigger re-render (store just pathname, not query params)
    setPathname(newPathname);
  };

  return (
    <NavigationContext.Provider value={{ pathname, navigate }}>
      {children}
    </NavigationContext.Provider>
  );
};

// Custom hook for using navigation
export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

export default NavigationContext;